// An Advanced picture's structure as live names it (`TCS_TEXTS` and `TCS_VISUALS`,
// `constants/images.tsx`): its texts and its visuals, each with live's label, icon and placement,
// in live's order. The library preview's "Image Structure" shows and edits them (spec
// docs/specs/web/library-image-structure.md), and the ad copies' sections are its texts.
import {
  ArrowLeftRight,
  Badge,
  BadgeCheck,
  Camera,
  CheckCheck,
  Crop,
  Flame,
  Grid2x2,
  Heading1,
  Heading2,
  Image as ImageIcon,
  ListCheck,
  MessageCircleQuestionMark,
  MessageSquareQuote,
  Percent,
  Pipette,
  Sparkles,
  SquareSquare,
  Stamp,
  Target,
  Users,
  type LucideIcon,
} from "lucide-react";

/** A text of a structure, as the API names it (`text_elements`). */
export type TextKey =
  | "headline"
  | "subheadline"
  | "testimonial"
  | "discount"
  | "call_to_action"
  | "value_proposition"
  | "badge"
  | "feature"
  | "trust_signal"
  | "benefit_bullets";

/** A visual of a structure, as the API names it (`visual_style`). */
export type VisualKey =
  | "hero_product_image"
  | "product_grouping"
  | "lifestyle_photography"
  | "color_blocking"
  | "arrows_connectors"
  | "white_space"
  | "people"
  | "circular_badges"
  | "problem_visualization"
  | "background_treatment"
  | "decorative_elements";

/** An element of a structure: its key, live's label and icon, and the key of its placement. */
export interface StructureElement<Key extends string> {
  key: Key;
  label: string;
  icon: LucideIcon;
  placement: `${Key}_placement`;
}

const element = <Key extends string>(key: Key, label: string, icon: LucideIcon): StructureElement<Key> => ({
  key,
  label,
  icon,
  placement: `${key}_placement`,
});

/** Live's texts, in its order (`TCS_TEXTS`, `TCS_TEXT_RENDER_DATA`); the ad copies' sections too. */
export const TEXT_ELEMENTS: readonly StructureElement<TextKey>[] = [
  element("headline", "Primary Headline", Heading1),
  element("subheadline", "Subheadline", Heading2),
  element("testimonial", "Testimonial", MessageSquareQuote),
  element("discount", "Discount", Percent),
  element("call_to_action", "CTAs", Target),
  element("value_proposition", "Value Props", CheckCheck),
  element("badge", "Promotional Badges", BadgeCheck),
  element("feature", "Feature Callouts", Flame),
  element("trust_signal", "Certifications", Stamp),
  element("benefit_bullets", "Benefit Checklist", ListCheck),
];

/** Live's visuals, in its order (`TCS_VISUALS`, `TCS_VISUAL_RENDER_DATA`). */
export const VISUAL_ELEMENTS: readonly StructureElement<VisualKey>[] = [
  element("hero_product_image", "Hero Product Image", ImageIcon),
  element("product_grouping", "Product Grouping", Grid2x2),
  element("lifestyle_photography", "Lifestyle", Camera),
  element("color_blocking", "Color Blocking", Pipette),
  element("arrows_connectors", "Arrows / Connectors", ArrowLeftRight),
  element("white_space", "White Space", Crop),
  element("people", "People", Users),
  element("circular_badges", "Circular Badges", Badge),
  element("problem_visualization", "Problem Visualization", MessageCircleQuestionMark),
  element("background_treatment", "Background", SquareSquare),
  element("decorative_elements", "Decorative Elements", Sparkles),
];

/** An element a structure holds a text for: the element, its text and its placement ("" for none). */
export interface ShownElement<Key extends string> {
  element: StructureElement<Key>;
  value: string;
  placement: string;
}

/** A part of a structure (`text_elements` or `visual_style`): each element's text and placement. */
export type StructurePart<Key extends string> = Partial<Record<Key | `${Key}_placement`, string | null>>;

/** The elements a part of a structure holds a text for, in live's order (live's `buildEntries`). */
export function shownElements<Key extends string>(
  elements: readonly StructureElement<Key>[],
  part: StructurePart<Key> | null | undefined,
): ShownElement<Key>[] {
  return elements.flatMap((element) => {
    const value = (part?.[element.key] ?? "").trim();
    return value === "" ? [] : [{ element, value, placement: (part?.[element.placement] ?? "").trim() }];
  });
}
